import { createSelector } from 'reselect';
import { clientOnlyChild } from '../../../selectors/clients';

/**
 * Sélecteurs de base
 */
const EMPTY_LIST = Object.freeze([]);

const getMouvementsOr = state => state.mouvementsOr?.liste || EMPTY_LIST;
const getMouvementsOrClient = state => state.mouvementsOrClient?.liste || EMPTY_LIST;
const getMouvementsClal = state => state.mouvementsClal?.liste || EMPTY_LIST;
const getFournisseurs = state => state.generalData?.listeFournisseur || EMPTY_LIST;
const getCours = state => state.cours?.listeCours || EMPTY_LIST;
const getListeMetaux = state => state.generalData?.listeMetaux || EMPTY_LIST;
const getListeMetal = state => state.generalData?.listeMetal || EMPTY_LIST;

/**
 * Sélecteur mémorisé pour les données brutes des mouvements
 */
export const selectRawMovementsData = createSelector(
  [getMouvementsOr, getMouvementsOrClient, getMouvementsClal],
  (mouvements, mouvementsClient, mouvementsClal) => {
    const fournisseursRows = (mouvements || [])
      .filter(r => r.fournisseur_id && !r.client_id)
      .map(r => ({ ...r, __source: 'FOUR' }));
    
    const clientsRows = (mouvementsClient || [])
      .filter(r => r.client_id && !r.fournisseur_id)
      .map(r => ({ ...r, __source: 'CLI' }));
    
    const clalNorm = (mouvementsClal || [])
      .map(r => ({ ...r, __source: 'CLAL' }));
    
    return { fournisseursRows, clientsRows, clalNorm };
  }
);

/**
 * Sélecteur mémorisé pour la liste des métaux unifiée
 */
export const selectMetauxList = createSelector(
  [getListeMetal, getListeMetaux],
  (listeMetal, listeMetaux) => {
    const source = (listeMetal && listeMetal.length) ? listeMetal : listeMetaux;
    return source.map(m => ({
      id_metal: String(m.id_metal || m.id || m.metal_id),
      description: m.description || m.label || m.nom || m.name || `Métal ${m.id_metal || m.id || ''}`
    }));
  }
);

/**
 * Sélecteur mémorisé pour les fournisseurs filtrés
 */
export const selectFiltredFournisseurs = createSelector(
  [getFournisseurs],
  (fournisseurs) => {
    return (fournisseurs || [])
      .filter(f => Number(f.type_four) === 998 && Number(f.actif) === 1)
      .map(f => {
        const fid = f.id ?? f.four_id ?? f.fournisseur_id;
        return { 
          ...f, 
          id: fid, 
          label: f.raison_soc || f.label || `${fid}` 
        };
      });
  }
);

/**
 * Sélecteur mémorisé pour les clients
 */
export const selectClients = createSelector(
  [clientOnlyChild],
  (listeClientInput) => {
    return (listeClientInput || []).map(c => {
      const cid = c.id ?? c.client_id ?? c.cli_id;
      const label = c.label || c.raison_soc || c.nom || c.name || 
                   c.denomination || (cid ? `Client ${cid}` : 'Client');
      return { ...c, id: cid, label };
    });
  }
);

/**
 * Sélecteur mémorisé pour le cours de perte client
 */
export const selectPerteCoursClient = createSelector(
  [getCours],
  (cours) => {
    const found = cours.find(c => String(c.id_cours) === '14');
    if (!found) return null;
    return Number(found.valeur) || 0;
  }
);

/**
 * Sélecteur mémorisé pour le cours vieil or
 */
export const selectCoursVieilOr = createSelector(
  [getCours],
  (cours) => {
    const found = cours.find(c => String(c.id_cours) === '15');
    return found ? (Number(found.valeur) || 0) : null;
  }
);

/**
 * Mapper tous les sélecteurs pour mapStateToProps
 */
export const mapStateToPropsSelectors = state => ({
  rawMovementsData: selectRawMovementsData(state),
  metauxList: selectMetauxList(state),
  filtFournisseurs: selectFiltredFournisseurs(state),
  filtClients: selectClients(state),
  perteCoursClient: selectPerteCoursClient(state),
  coursVieilOr: selectCoursVieilOr(state)
});
